import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Send, User, Building2, Dumbbell } from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

export default function RatingModule({ user, targetType, targetId, targetName }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "evaluations"]);

  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [averageRating, setAverageRating] = useState(0);

  useEffect(() => {
    if (targetId) {
      fetchRatings();
    }
  }, [targetId]);

  const fetchRatings = async () => {
    try {
      const response = await fetch(`${API}/ratings/${targetType}/${targetId}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure ratings is always an array
        const ratingsArray = Array.isArray(data) ? data : (data.ratings || []);
        setRatings(ratingsArray);

        // Calculate average
        if (ratingsArray.length > 0) {
          const avg = ratingsArray.reduce((sum, r) => sum + r.rating, 0) / ratingsArray.length;
          setAverageRating(avg);
        }
      }
    } catch (error) {
      console.error("Error fetching ratings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRating = async () => {
    if (selectedRating === 0) {
      toast.error(t("common:toast_lutfenBirPuanSecin"));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          review_type: targetType,
          target_id: targetId,
          rating: selectedRating,
          comment: comment || null,
        }),
      });

      if (response.ok) {
        toast.success(t("evaluations:toast_degerlendirmenizKaydedildi"));
        fetchRatings();
        setShowModal(false);
        setSelectedRating(0);
        setComment("");
      } else {
        const error = await response.json();
        toast.error(error.detail || t("evaluations:reviewSaveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const getTargetIcon = () => {
    switch (targetType) {
      case "business":
        return Building2;
      case "trainer":
        return Dumbbell;
      case "member":
        return User;
      default:
        return Star;
    }
  };

  const TargetIcon = getTargetIcon();

  const renderStars = (rating, interactive = false, size = "w-5 h-5") => {
    return [...Array(5)].map((_, i) => (
      <button
        key={i}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && setSelectedRating(i + 1)}
        className={interactive ? "cursor-pointer" : "cursor-default"}
      >
        <Star
          className={`${size} transition-colors ${
            i < rating
              ? "text-yellow-500 fill-yellow-500"
              : "text-grey-300"
          } ${interactive && "hover:text-yellow-400"}`}
        />
      </button>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-navy/10 rounded-xl flex items-center justify-center">
                <TargetIcon className="w-7 h-7 text-navy" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-navy">
                  {targetName}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex">{renderStars(Math.round(averageRating))}</div>
                  <span className="font-bold text-navy">{averageRating.toFixed(1)}</span>
                  <span className="text-grey text-sm">({ratings.length} değerlendirme)</span>
                </div>
              </div>
            </div>

            <Button
              className="bg-navy hover:bg-navy/90 rounded-xl"
              onClick={() => setShowModal(true)}
              data-testid="add-rating-btn"
            >
              <Star className="w-4 h-4 mr-2" />
              Değerlendir
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Rating Distribution */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">{t("common:puanDagilimi")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = ratings.filter((r) => r.rating === star).length;
              const percentage = ratings.length > 0 ? (count / ratings.length) * 100 : 0;

              return (
                <div key={star} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-12">
                    <span className="text-sm font-medium">{star}</span>
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                  </div>
                  <div className="flex-1 h-2 bg-grey-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-500 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-grey w-8">{count}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Reviews List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Yorumlar</CardTitle>
        </CardHeader>
        <CardContent>
          {ratings.length > 0 ? (
            <div className="space-y-4">
              {ratings.slice(0, 10).map((rating) => (
                <div
                  key={rating.rating_id}
                  className="p-4 bg-slate-50 rounded-xl"
                  data-testid={`rating-${rating.rating_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-navy text-white text-xs">
                          {rating.user_name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{rating.user_name || "Üye"}</p>
                        <p className="text-xs text-grey">
                          {rating.created_at
                            ? format(new Date(rating.created_at), "dd/MM/yyyy", {
                                locale: getDateLocale(),
                              })
                            : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex">{renderStars(rating.rating, false, "w-4 h-4")}</div>
                  </div>
                  {rating.comment && (
                    <p className="text-grey-700 text-sm">{rating.comment}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              <Star className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>{t("evaluations:henuzDegerlendirmeYapilmamis")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rating Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('common:makeEvaluation')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="text-center">
              <p className="text-grey mb-3">{targetName} için puanınız</p>
              <div className="flex justify-center gap-2">
                {renderStars(selectedRating, true, "w-10 h-10")}
              </div>
              {selectedRating > 0 && (
                <p className="text-sm text-navy mt-2 font-medium">
                  {selectedRating === 1 && t('messaging:poor')}
                  {selectedRating === 2 && t('messaging:bad')}
                  {selectedRating === 3 && t('messaging:okay')}
                  {selectedRating === 4 && t('messaging:good')}
                  {selectedRating === 5 && t('messaging:excellent')}
                </p>
              )}
            </div>

            <div>
              <Textarea
                placeholder="Yorumunuz (opsiyonel)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="rounded-xl min-h-24"
                data-testid="rating-comment"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowModal(false)}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleSubmitRating}
                disabled={submitting || selectedRating === 0}
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Gönder
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
